
import { useState } from 'react';

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: string;
}

export default function FAQSection() {
  const [activeId, setActiveId] = useState<number | null>(1);

  const faqData: FAQItem[] = [
    {
      id: 1,
      question: 'Web sitesi tasarım süreci nasıl işliyor?',
      answer: 'Web sitesi tasarım sürecimiz 6 aşamadan oluşur: Keşif ve analiz, planlama ve strateji, tasarım ve prototipleme, geliştirme ve kodlama, test ve optimizasyon, son olarak yayınlama ve destek. Her aşamada müşterimizle yakın işbirliği halinde çalışırız.',
      category: 'Genel'
    },
    {
      id: 2,
      question: 'Bir web sitesi projesi ne kadar sürer?',
      answer: 'Proje süresi, sitenin karmaşıklığına ve özelliklerine bağlı olarak değişir. Basit kurumsal siteler 2-4 hafta, e-ticaret siteleri 4-8 hafta, özel geliştirmeler ise 8-16 hafta sürebilir. Detaylı zaman planını proje başında sizinle paylaşırız.',
      category: 'Genel'
    },
    {
      id: 3,
      question: 'Web sitesi yenileme hizmeti veriyor musunuz?',
      answer: 'Evet, mevcut web sitelerinin yenilenmesi konusunda da hizmet veriyoruz. Sitenizin mevcut durumunu analiz ederek, modern tasarım ve teknolojilerle yeniliyoruz. Mevcut içeriklerinizi koruyarak güvenli bir geçiş sağlarız.',
      category: 'Hizmetler'
    },
    {
      id: 4,
      question: 'E-ticaret sitesi için hangi ödeme yöntemlerini entegre ediyorsunuz?',
      answer: 'Tüm popüler ödeme yöntemlerini entegre edebiliriz: Kredi kartı (Visa, MasterCard), banka kartları, PayPal, iyzico, PayTR, Garanti Sanal Pos ve diğer yerel ödeme sağlayıcıları. Güvenli ödeme altyapısı sağlarız.',
      category: 'E-Ticaret'
    },
    {
      id: 5,
      question: 'Mobil uyumlu tasarım sağlıyor musunuz?',
      answer: 'Elbette! Tüm web sitelerimiz responsive tasarım ile gelir. Siteniz masaüstü, tablet ve mobil cihazlarda mükemmel görünür ve çalışır. Mobil-first yaklaşımı ile tasarım yapıyoruz.',
      category: 'Tasarım'
    },
    {
      id: 6,
      question: 'SEO hizmetleri hangi süreçleri içeriyor?',
      answer: 'SEO hizmetlerimiz: anahtar kelime analizi, sayfa içi optimizasyon, teknik SEO, içerik optimizasyonu, site hızı optimizasyonu, Google Analytics kurulumu ve aylık raporlama içerir. Arama motorlarında üst sıralara çıkmanızı sağlarız.',
      category: 'SEO'
    },
    {
      id: 7,
      question: 'Proje sonrası destek hizmeti veriyor musunuz?',
      answer: '6 ay ücretsiz teknik destek, güvenlik güncellemeleri ve küçük düzeltmeler dahildir. Sonrasında aylık veya yıllık bakım paketleri sunuyoruz. 7/24 acil destek hattımız da mevcuttur.',
      category: 'Destek'
    },
    {
      id: 8,
      question: 'Fiyatlarınız nasıl belirleniyor?',
      answer: 'Fiyatlarımız proje kapsamına, karmaşıklığına ve özelliklerine göre belirlenir. Sabit paket fiyatlarımız vardır ancak özel projeler için özelleştirilmiş teklif hazırlarız. İlk danışmanlığımız ücretsizdir.',
      category: 'Fiyatlandırma'
    },
    {
      id: 9,
      question: 'Hangi teknolojileri kullanıyorsunuz?',
      answer: 'Modern web teknolojilerini kullanırız: React, Next.js, Vue.js, Node.js, PHP, Python, HTML5, CSS3, JavaScript, TypeScript. Veritabanı için MySQL, PostgreSQL, MongoDB tercih ederiz. Cloud hosting için AWS, Google Cloud kullanırız.',
      category: 'Teknoloji'
    },
    {
      id: 10,
      question: 'Domain ve hosting hizmeti veriyor musunuz?',
      answer: 'Evet, domain registrasyonu ve hosting hizmeti de veriyoruz. Türkiye ve uluslararası domain uzantıları, SSD hosting, SSL sertifikası, günlük yedekleme ve 99.9% uptime garantisi sunuyoruz.',
      category: 'Hosting'
    }
  ];

  const categories = ['Tümü', ...Array.from(new Set(faqData.map(faq => faq.category)))];
  const [activeCategory, setActiveCategory] = useState('Tümü');

  const filteredFAQs = activeCategory === 'Tümü' 
    ? faqData 
    : faqData.filter(faq => faq.category === activeCategory);

  const toggleFAQ = (id: number) => {
    setActiveId(activeId === id ? null : id);
  };

  return (
    <section className="py-20 bg-gray-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-blue-500/3 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-0 w-64 h-64 bg-cyan-500/3 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-blue-500/20 backdrop-blur-sm rounded-full border border-blue-400/30 mb-6">
            <i className="ri-question-line text-blue-400 mr-2"></i>
            <span className="text-blue-400 text-sm font-medium">Sık Sorulan Sorular</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Merak Ettikleriniz
            </span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            En çok sorulan soruların yanıtlarını burada bulabilirsiniz. 
            Başka sorularınız varsa bizimle iletişime geçmekten çekinmeyin.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap ${
                activeCategory === category
                  ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white'
                  : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50 hover:text-white border border-gray-700/50'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* FAQ Items */}
        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {filteredFAQs.map((faq, index) => (
              <div
                key={faq.id}
                className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 overflow-hidden transition-all duration-300 hover:border-blue-500/30"
              >
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-gray-800/30 transition-colors duration-300"
                >
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className="text-blue-400 text-sm font-medium mr-3 px-2 py-1 bg-blue-500/20 rounded-lg">
                        {faq.category}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-white pr-4">
                      {faq.question}
                    </h3>
                  </div>
                  <div className={`flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-full transition-all duration-300 ${
                    activeId === faq.id 
                      ? 'bg-blue-500 text-white rotate-180' 
                      : 'bg-gray-700 text-gray-400'
                  }`}>
                    <i className="ri-arrow-down-s-line"></i>
                  </div>
                </button>
                
                <div className={`overflow-hidden transition-all duration-300 ${
                  activeId === faq.id ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}>
                  <div className="px-6 pb-6">
                    <div className="bg-gray-800/30 rounded-xl p-4 border-l-4 border-blue-500">
                      <p className="text-gray-300 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Contact CTA */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 backdrop-blur-sm rounded-2xl border border-blue-400/20 p-8 max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">
              Sorunuzun Cevabını Bulamadınız mı?
            </h3>
            <p className="text-gray-300 mb-6">
              Uzman ekibimiz size yardımcı olmak için hazır. 
              Hemen iletişime geçin ve ücretsiz danışmanlık alın.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:0506099396"
                className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium rounded-lg transition-all duration-300 hover:scale-105"
              >
                <i className="ri-phone-line mr-2"></i>
                Hemen Arayın
              </a>
              <button
                onClick={() => {
                  const element = document.getElementById('iletisim');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="inline-flex items-center justify-center px-6 py-3 bg-gray-800 hover:bg-gray-900 text-white font-medium rounded-lg border border-gray-700 transition-all duration-300 hover:scale-105"
              >
                <i className="ri-mail-line mr-2"></i>
                Mesaj Gönderin
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
